import type { ErrorEvent, EventHint } from '@sentry/nextjs';

import { CHUNK_LOAD_IGNORE_ERRORS } from '../chunk-load-error';
import { TRANSLATED_DOM_MUTATION_MESSAGE } from '../exception-context';
import { applySentryGrouping } from './grouping';

/**
 * Stale-deploy chunk failures, applied from the browser Sentry init.
 *
 * The server and Edge inits do not see a missing `/_next` chunk. Keeping the
 * list here, next to the other Sentry filters, means the browser init and
 * the tests share one definition.
 */
export const SENTRY_IGNORED_CHUNK_LOAD_ERRORS = CHUNK_LOAD_IGNORE_ERRORS;

/**
 * Translator parent mismatches, applied from the browser Sentry init.
 *
 * The client guard prevents the throw. This is the backup when a
 * NotFoundError from removeChild or insertBefore still reaches Sentry.
 */
export const SENTRY_IGNORED_TRANSLATED_DOM_ERRORS: RegExp[] = [
  TRANSLATED_DOM_MUTATION_MESSAGE,
];

/**
 * Shared Sentry settings for the browser, Node.js server and Edge runtimes.
 *
 * Sentry stays off unless `NEXT_PUBLIC_MONITORING_PROVIDER=sentry` and
 * `NEXT_PUBLIC_SENTRY_DSN` are both set, so preview and local builds without
 * those variables behave exactly as before.
 *
 * Data collection is deliberately left at the SDK's conservative default (no
 * `dataCollection` / `sendDefaultPii`): request bodies, cookies and AI prompts
 * here carry customer supply-chain data, and error reports do not need them.
 */
export function isSentryEnabled() {
  return (
    process.env.NEXT_PUBLIC_MONITORING_PROVIDER === 'sentry' &&
    Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN)
  );
}

/**
 * Script URLs that count as our own code in the browser: the Next.js bundle
 * served from `/_next/`, on supplyautomate.com, a Vercel preview, or localhost.
 */
export const SENTRY_ALLOWED_SCRIPT_URLS: RegExp[] = [
  /^https?:\/\/(www\.)?supplyautomate\.com\//,
  /^https?:\/\/[^/]+\.vercel\.app\//,
  /^https?:\/\/localhost(:\d+)?\//,
];

export function getSentryBaseOptions() {
  return {
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment:
      process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
      process.env.NEXT_PUBLIC_VERCEL_ENV ??
      process.env.VERCEL_ENV ??
      process.env.NODE_ENV,
    // 100% of traces locally, 10% in production.
    tracesSampleRate: process.env.NODE_ENV === 'development' ? 1.0 : 0.1,
    /*
     * Framework-only stacks all look the same, so Sentry would group unrelated
     * server errors together. This gives those a message fingerprint, and
     * marks gateway timeouts as one transient warning.
     */
    beforeSend(event: ErrorEvent, hint: EventHint) {
      return applySentryGrouping(event, hint);
    },
  };
}
